import { Link } from '@tanstack/react-router';
import { ErroreApi } from '../api/cliente.js';
import { useState } from 'react';
import { useCancellaVista, useSalvaVista, useViste } from '../api/anagrafiche.js';
import {
  RICERCA_ARTICOLI,
  type RigaArticolo,
  esportaArticoli,
  useArticoli,
  useDefinizioneFiltriArticoli,
} from '../api/articoli.js';
import { AzioniDiMassa } from '../componenti/AzioniDiMassa.js';
import { BarraFiltri } from '../componenti/BarraFiltri.js';
import { PiedeElenco } from '../componenti/PiedeElenco.js';
import { type Colonna, Tabella } from '../componenti/Tabella.js';
import { useRicerca } from '../useRicerca.js';
import { useSelezione } from '../useSelezione.js';
import { ModaleNuovoArticolo } from './ModaleNuovoArticolo.js';
import { locale, t } from '../i18n/lingua.js';

/**
 * L'elenco degli articoli.
 *
 * I sei attributi che descrivono un articolo — linea, prodotto, tipologia,
 * confezionamento, formato, variante — nel v2 sono sei anagrafiche identiche
 * con sei maschere identiche. Qui sono sei filtri che si comportano allo
 * stesso modo, e in elenco sono colonne: si vede subito perché due articoli
 * che sembrano uguali sono due articoli diversi.
 */

/** Il segnaposto del legacy: 96 articoli ce l'hanno al posto dello SKU. */
export const SKU_ASSENTE = '-----';

export function nomeArticolo(a: {
  description: string;
  sku: string;
  bcNumber?: string | null;
  id: number;
}): string {
  if (a.description) return a.description;
  if (a.sku && a.sku !== SKU_ASSENTE) return a.sku;
  return a.bcNumber || t('Articolo {n}', { n: a.id });
}

const COLONNE: Colonna<RigaArticolo>[] = [
  {
    chiave: 'description',
    titolo: t('Descrizione'),
    ordinabile: true,
    larghezzaMax: 320,
    cella: (r) => (
      <>
        <Link className="cell-link strong" to="/items/$id" params={{ id: String(r.id) }}>
          {/* Alcuni articoli non hanno né descrizione né SKU vero: senza un
              ripiego la riga sarebbe un collegamento invisibile. Si ripiega
              sul numero BC, che è la chiave e c'è sempre — non sul segnaposto
              `-----`, che non identifica niente. */}
          {nomeArticolo(r)}
        </Link>
        {r.archived && (
          <span className="badge" style={{ marginLeft: 8 }}>
            {t('archiviato')}
          </span>
        )}
      </>
    ),
  },
  {
    chiave: 'sku',
    titolo: 'SKU',
    ordinabile: true,
    monospazio: true,
    larghezzaMax: 260,
    // il segnaposto del legacy non è un codice: dirlo evita di cercarlo
    cella: (r) =>
      r.sku && r.sku !== SKU_ASSENTE ? r.sku : <span className="dim">{t('senza SKU')}</span>,
  },
  { chiave: 'bcNumber', titolo: t('Numero BC'), ordinabile: true, monospazio: true },
  { chiave: 'line', titolo: t('Linea'), ordinabile: true },
  { chiave: 'product', titolo: t('Prodotto'), larghezzaMax: 200 },
  { chiave: 'typology', titolo: t('Tipologia') },
  { chiave: 'packaging', titolo: t('Confezionamento'), larghezzaMax: 160 },
  { chiave: 'format', titolo: t('Formato'), monospazio: true },
  { chiave: 'variant', titolo: t('Variante'), larghezzaMax: 180 },
  { chiave: 'unit', titolo: t('UdM'), monospazio: true },
  {
    chiave: 'gsCode',
    titolo: t('Codice GS1'),
    monospazio: true,
    cella: (r) => r.gsCode ?? <span className="dim">—</span>,
  },
];

export function Articoli() {
  const { ricerca, testo, setTesto, cambia, vaiA, ordinaPer, filtra, applica } =
    useRicerca(RICERCA_ARTICOLI);
  const [nomeVista, setNomeVista] = useState('');
  const [salvataggioAperto, setSalvataggioAperto] = useState(false);
  const [nuovoAperto, setNuovoAperto] = useState(false);

  const elenco = useArticoli(ricerca);
  const [selezione, setSelezione] = useSelezione(elenco.data?.rows);
  const definizioni = useDefinizioneFiltriArticoli();
  const viste = useViste('items');
  const salvaVista = useSalvaVista('items');
  const cancellaVista = useCancellaVista('items');

  const dati = elenco.data;

  const salva = () => {
    const nome = nomeVista.trim();
    if (!nome) return;
    salvaVista.mutate(
      { name: nome, definition: { ...ricerca, page: 1 } },
      {
        onSuccess: () => {
          setNomeVista('');
          setSalvataggioAperto(false);
        },
      },
    );
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Articoli')}</h1>
          <div className="page-sub">
            {dati
              ? ricerca.archived
                ? t('{n} articoli, archiviati compresi · arrivano da Business Central', {
                    n: dati.total.toLocaleString(locale()),
                  })
                : t('{n} articoli · arrivano da Business Central', { n: dati.total.toLocaleString(locale()) })
              : elenco.error
                ? t('Elenco non disponibile')
                : t('Un momento…')}
          </div>
        </div>
        <div className="page-actions">
          <button
            className="btn"
            // con un filtro sbagliato l'elenco dice già perché: il file aprirebbe il JSON dell'errore
            disabled={Boolean(elenco.error)}
            onClick={() => esportaArticoli(ricerca, 'csv')}>
            CSV
          </button>
          <button
            className="btn"
            // con un filtro sbagliato l'elenco dice già perché: il file aprirebbe il JSON dell'errore
            disabled={Boolean(elenco.error)}
            onClick={() => esportaArticoli(ricerca, 'xlsx')}>
            Excel
          </button>
          <button className="btn btn-primary" onClick={() => setNuovoAperto(true)}>
            {t('Nuovo articolo')}
          </button>
        </div>
      </div>

      <div className="filterbar">
        <div className="presets">
          {viste.data?.views.map((vista) => (
            <span key={vista.id} className="preset-con-x">
              <button
                className="preset"
                onClick={() => applica(vista.definition)}
                title={vista.mine ? t('Vista tua') : t('Condivisa da un collega')}
              >
                {vista.name}
              </button>
              {vista.mine && (
                // un pulsante suo, accanto a quello della vista: dentro l'altro
                // pulsante, dalla tastiera non ci si arrivava
                <button
                  className="chip-x preset-x"
                  aria-label={t('Elimina la vista {nome}', { nome: vista.name })}
                  onClick={() => cancellaVista.mutate(vista.id)}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          {salvataggioAperto ? (
            <span className="row" style={{ gap: 4 }}>
              <input
                className="input"
                style={{ height: 26, width: 170 }}
                placeholder={t('Nome della vista')}
                // il servizio ne accetta 60: oltre, la vista non si salvava e niente lo diceva
                maxLength={60}
                value={nomeVista}
                autoFocus
                onChange={(e) => setNomeVista(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') salva();
                  if (e.key === 'Escape') setSalvataggioAperto(false);
                }}
              />
              <button className="btn" onClick={salva} disabled={salvaVista.isPending}>
                {t('Salva')}
              </button>
              {salvaVista.error instanceof ErroreApi && (
                <span className="hint" style={{ color: 'var(--tomato)' }}>
                  {salvaVista.error.message}
                </span>
              )}
            </span>
          ) : (
            <button
              className="preset btn-ghost"
              style={{ fontSize: 12 }}
              onClick={() => setSalvataggioAperto(true)}
            >
              {t('+ Salva questa vista')}
            </button>
          )}
        </div>

        <span className="omnisearch" style={{ maxWidth: 340, flex: '0 1 340px' }}>
          <span>⌕</span>
          <input
            className="input"
            style={{ border: 'none', height: 22, padding: 0, background: 'transparent' }}
            placeholder={t('Cerca per SKU, descrizione, numero BC o codice GS1')}
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
          />
        </span>

        <button
          className={ricerca.archived ? 'chip chip-set' : 'chip'}
          onClick={() => cambia({ archived: !ricerca.archived })}
        >
          {ricerca.archived ? t('Con gli archiviati') : 'Mostra archiviati'}
        </button>

        <BarraFiltri
          definizioni={definizioni.data?.filters ?? []}
          opzioni={definizioni.data?.options ?? {}}
          valori={ricerca.filters}
          onCambia={filtra}
        />
      </div>

      <AzioniDiMassa
        percorso="/api/items/bulk"
        nome="articoli"
        selezione={selezione}
        conRipristina={ricerca.archived}
        chiaviDaRileggere={['articoli']}
        onFatto={() => setSelezione(new Set())}
        onAnnulla={() => setSelezione(new Set())}
      />

      <div className="table-wrap" style={{ marginTop: 12, marginBottom: 24 }}>
        <Tabella
          colonne={COLONNE}
          righe={dati?.rows ?? []}
          chiaveRiga={(r) => r.id}
          ordina={ricerca.sort}
          verso={ricerca.dir}
          onOrdina={ordinaPer}
          selezione={selezione}
          onSelezione={setSelezione}
          caricamento={elenco.isFetching}
          errore={elenco.error}
          vuoto={
            ricerca.q ? t('Nessun articolo per «{cerca}».', { cerca: ricerca.q }) : t('Nessun articolo con questi filtri.')
          }
        />
        <PiedeElenco
          pagina={dati?.page ?? 1}
          pagine={dati?.pages ?? 1}
          totale={dati?.total ?? 0}
          dimensione={ricerca.size}
          nomePlurale={t('articoli')}
          onPagina={vaiA}
          onDimensione={(size) => cambia({ size })}
        />
      </div>

      {nuovoAperto && <ModaleNuovoArticolo onChiudi={() => setNuovoAperto(false)} />}
    </>
  );
}
